import { z } from 'zod';

/** Client documents on a drafting matter (ADR-044). Built switched off; see its gates. */

/** 50 MB per file, no page limit, 30 files per matter (owner decisions, ADR-044). */
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;
export const DOCUMENTS_PER_MATTER = 30;
/** Documents expire this many months after the matter last changed. */
export const DOCUMENT_RETENTION_MONTHS = 24;
/** The expiry date is shown from this many days before. */
export const DOCUMENT_EXPIRY_WARNING_DAYS = 30;

export const DocumentKindSchema = z.enum(['pdf', 'docx', 'text']);
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

/** Whether Buakei could read the text: pending, read, a PDF with no text layer, or failed. */
export const DocumentReadingSchema = z.enum(['pending', 'read', 'no_text', 'failed']);
export type DocumentReading = z.infer<typeof DocumentReadingSchema>;

export const DOCUMENT_READING_LABEL: Record<DocumentReading, string> = {
  pending: 'Being read',
  read: 'Read',
  no_text: 'No readable text (scanned)',
  failed: 'Could not be read',
};

export const MatterDocumentSchema = z
  .object({
    id: z.string(),
    matterId: z.string(),
    name: z.string(),
    kind: DocumentKindSchema,
    size: z.number().int().nonnegative(),
    /** PDF pages; null for Word and text files. */
    pages: z.number().int().nonnegative().nullable(),
    reading: DocumentReadingSchema,
    uploadedByName: z.string().nullable(),
    createdAt: z.string(),
    /** When it will be deleted unless the matter changes (24 months after the last change). */
    expiresAt: z.string(),
    canDelete: z.boolean(),
  })
  .strict();
export type MatterDocument = z.infer<typeof MatterDocumentSchema>;

export const MatterDocumentListSchema = z
  .object({
    documents: z.array(MatterDocumentSchema),
    limit: z.number().int(),
    maxBytes: z.number().int(),
  })
  .strict();
export type MatterDocumentList = z.infer<typeof MatterDocumentListSchema>;

export const DocumentPurgeResultSchema = z
  .object({ checked: z.number().int(), deleted: z.number().int() })
  .strict();
export type DocumentPurgeResult = z.infer<typeof DocumentPurgeResultSchema>;

/**
 * Research documents (ADR-045): a lawyer's own documents in a professional workspace, private to
 * them, which Ask Buakei can read to answer questions about them.
 */
export const RESEARCH_DOCUMENTS_PER_PERSON = 30;
/** At most this many documents may be attached to one question. */
export const DOCUMENTS_PER_QUESTION = 5;

export const ResearchDocumentSchema = MatterDocumentSchema.omit({
  matterId: true,
  uploadedByName: true,
  canDelete: true,
}).strict();
export type ResearchDocument = z.infer<typeof ResearchDocumentSchema>;

export const ResearchDocumentListSchema = z
  .object({
    documents: z.array(ResearchDocumentSchema),
    limit: z.number().int(),
    maxBytes: z.number().int(),
  })
  .strict();
export type ResearchDocumentList = z.infer<typeof ResearchDocumentListSchema>;
